import {
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	ElementRef,
	Inject,
	Injector,
	OnDestroy,
	OnInit,
	Optional,
	ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpStatusCode } from '@angular/common/http';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import {
	BehaviorSubject,
	combineLatest,
	EMPTY,
	filter,
	Observable,
	of,
	Subject,
	switchMap
} from 'rxjs';
import { takeUntil, skip, finalize, catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { GoPulseTourService } from '@go-modules/services/go-pulse-tour/go-pulse-tour.service';
import {
	ACTIVITY_TASK_LEARNER_TOUR_KEY,
	createActivityTaskLearnerTour,
	createPulseIncompleteTaskStep
} from '@go-modules/services/go-pulse-tour/constants/activity-task-tour-learner';
import { ActivatedRoute } from '@angular/router';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ScoreFilter } from '@go-modules/enums/score-filter';
import { SortOrder, SortOrderTranslations } from '@go-modules/enums/sort-order-main';
import { MinePeerFilter } from '@go-modules/enums/mine-peer-filter';
import { VIEWS } from '@go-modules/enums/views';
import { ViewStateDataSource } from '@go-modules/services/view-state-datasource/view-state.datasource';
import { ZeroStateChangedEvent } from '@go-modules/components/data-source-zero-state/data-source-zero-state.component';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { Session as SessionModel, sessionToken } from 'go-modules/models/session/session.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import type { License } from '@go-modules/interfaces/licenses';
import * as dayjs from 'dayjs';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import {
	LicenseExpirationHandler
} from '@go-modules/services/license-expiration-handler/license-expiration-handler.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { NgxLicenseUpgradeService } from '@go-modules/services/license/license-upgrade/license-upgrade.service';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
	ConfirmDialogComponent,
	ConfirmDialogData
} from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { MediaSource } from 'go-modules/models/media';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { MODE as INSTRUCTIONS_MODE } from 'go-modules/modals/activity-instructions/modes';
import { FeedbackMode, MediaDisplayMode } from 'go-modules/feedback-session/feedback-session.provider';
import { SessionStorageService, sessionStorageToken } from 'go-modules/session-manager/session-storage.service';
import { equipmentCheckModalToken } from 'go-modules/modals/equipment-check/modal.factory';
import {
	ActivityInstructionsModal,
	activityInstructionsModalToken
} from 'go-modules/modals/activity-instructions/modal.factory';
import { StartTestDialogComponent } from '@go-modules/components/dialogs/start-test-dialog/start-test-dialog.component';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { noop } from 'angular';
import { sessionCreationWizardServiceToken } from 'go-modules/session-creation-wizard/wizard.service';
import { SessionCreationWizardService } from 'go-modules/session-creation-wizard';
import { $qToken } from '@go-modules/upgraded-3rd-party-deps/q.upgrade';
import { ActivityListDataSource } from '@go-modules/services/activity-list-datasource/activity-list.datasource';
import { FolderListDataSource } from '@go-modules/services/folder-list-datasource/folder-list-datasource';
import {
	ActivityInstructionsDialogComponent
} from '@go-modules/components/dialogs/activity-Instructions-dialog/activity-instructions-dialog.component';
import {
	InviteUsersDialogComponent
} from '@go-modules/components/dialogs/invite-users-dialog/invite-users-dialog.component';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { LearnFromFeedbackZeroState } from '@go-modules/components/activity-view/session-zero-state/session-zero-state.component';
import { NgxActivityViewComponent } from '@go-modules/components/activity-view/activity-view.component';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';

interface ActivityTaskSessionsGroupContext {
	hasInstructorRole?: (includeAdmins?: boolean) => boolean;
	hasPresenterRole?: () => boolean;
}

/**
 * In order to steamline the testing conversion from activity-view this was not made a standalone component.
 * At some point we'll probably want to update the tests so this can be a standalone component.
 */
@Component({
	selector: 'ngx-activity-task-sessions',
	templateUrl: './activity-task-sessions.component.html',
	styleUrl: './activity-task-sessions.component.scss',
	standalone: false
})
export class NgxActivityTaskSessionsComponent implements OnInit, OnDestroy {
	@ViewChild(MenuComponent) public menuComponent: MenuComponent;
	@ViewChild('activityContainer', {read: ElementRef}) public activityContainer: ElementRef;

	public activityId: number;
	public task: Task | null = null;
	public totalTaskCount = 0;
	public currentTaskPosition = 0;
	public currentView$: Observable<VIEWS>;
	public dataSourceZeroState: ZeroStateChangedEvent = {isLoading: true, hasZeroState: false, errorCode: null};
	public form: FormGroup;
	public hasFilteredSessions$: Observable<boolean>;
	public shouldShowTestBailedView$: Observable<boolean>;
	public canStartActivity$$ = new BehaviorSubject<boolean>(false);
	public getSessionCreationDisabledMessage$$ = new BehaviorSubject<string>('');
	public filteredSessions$: Observable<ReturnType<typeof SessionModel>[]>;
	public learnFromFeedbackZeroState$: Observable<LearnFromFeedbackZeroState | null> = EMPTY;
	public environmentVarsService: EnvironmentVarsService;
	public sortByOptions: SortOrder[] = Object.values(SortOrder).filter((option) => option !== SortOrder.DEFAULT);
	public shouldShowFooterButtons$: Observable<boolean>;
	public scoredFilter$: BehaviorSubject<ScoreFilter>;

	protected readonly sortOrderTranslations = SortOrderTranslations;
	protected readonly ScoreFilter = ScoreFilter;
	protected readonly views = VIEWS;
	protected readonly TaskType = TaskType;

	private destroy$ = new Subject<void>();
	private tasks: Task[] = [];

	private activityStarted$$ = new BehaviorSubject<boolean>(false);
	public selectedSessionIds$ = new BehaviorSubject<Set<number>>(new Set());
	public hasSubmittedSessions$ = new BehaviorSubject<boolean>(false);
	private submitSelectionInitialized = false;
	private submitSelectionDirty = false;
	private latestSessions: ReturnType<typeof SessionModel>[] = [];
	public isSubmittingSessions = false;
	public hasSelectedSessions = false;
	public isEditingSubmission = false;

	public taskType$ = new BehaviorSubject<TaskType | null>(null);
	private readonly currentTaskType = toSignal(this.taskType$, { initialValue: null });
	public readonly taskDetailsSubtitle = computed(() =>
		this.currentTaskType() === TaskType.React
			? 'common_peer-videos'
			: 'common_my-videos'
	);
	public isEditorContextMode = false;

	constructor (
		public sessionListDataSource: SessionListDataSource,
		public viewStateDataSource: ViewStateDataSource,
		public ngxSessionService: NgxSessionService,
		private taskService: NgxTaskService,
		private cdr: ChangeDetectorRef,
		private ngxActivityService: NgxActivityService,
		private licenseExpirationHandler: LicenseExpirationHandler,
		private featureFlag: NgxFeatureFlagService,
		private ngxLicenseUpgradeService: NgxLicenseUpgradeService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private modal: GoModalService,
		private activityListDataSource: ActivityListDataSource,
		private folderListDataSource: FolderListDataSource,
		private routerService: RouterService,
		public activityEditModeService: ActivityEditModeService,
		private route: ActivatedRoute,
		private pulseTourService: GoPulseTourService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(mediaToken) private MediaModel,
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		@Inject(sessionStorageToken) private sessionStorage: SessionStorageService,
		@Inject(equipmentCheckModalToken) private equipmentCheckModal,
		@Inject(activityInstructionsModalToken) private activityInstructionsModal: ActivityInstructionsModal,
		@Inject(sessionCreationWizardServiceToken) private sessionCreationWizardService: SessionCreationWizardService,
		@Inject($qToken) private $q,
		private ngxGoToastService: NgxGoToastService,
		private injector: Injector,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Optional() private activityViewComponent?: NgxActivityViewComponent
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
		this.form = new FormGroup({
			sortBy: new FormControl(SortOrder.NEWEST)
		});
		this.scoredFilter$ = new BehaviorSubject<ScoreFilter>(ScoreFilter.ALL);
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.canStartActivity$$.complete();
		this.taskType$.complete();
		this.pulseTourService.cleanupTour(ACTIVITY_TASK_LEARNER_TOUR_KEY);
	}

	public ngOnInit (): void {
		this.isEditorContextMode = this.editorContext != null;
		this.initializeTaskStreams();

		if (this.editorContext != null) {
			this.initializeEditorContextMode();
			return;
		}

		this.initializeSessionListControls();
		this.activityId = parseInt(this.routerService.getCurrentParams().activity_id, 10);
		this.taskType$.next(this.routerService.getCurrentParams().type as TaskType);

		this.route.params
			.pipe(
				skip(1), // Skip the initial emission since we handled it above
				takeUntil(this.destroy$)
			)
			.subscribe((params) => {
				const newTaskType = params.type as TaskType;
				if (newTaskType && newTaskType !== this.taskType$.value) {
					this.taskType$.next(newTaskType);
					this.applyTaskSelection(newTaskType, true);
				}
			});

		this.setMinePeerFilter();
		this.setExcludeUnsubmittedFilter();
		this.keepScoreFilterInSync();
		this.taskService.getTasks(this.activityId)
			// Replaced take(1) with takeUntil() so that tasks are updated when the task instructions are updated.
			.pipe(takeUntil(this.destroy$))
			.subscribe((tasks) => {
				this.updateTasks(tasks);
				this.applyTaskSelection(this.taskType$.value, false);
			});

		this.sessionListDataSource.connect()
			.pipe(takeUntil(this.destroy$))
			.subscribe((sessions) => {
				this.latestSessions = [...sessions];
				this.updateHasSubmittedSessions();
				this.syncSubmitSelectionState();
			});
		this.shouldShowTestBailedView$ = combineLatest([
			this.sessionListDataSource.connect(),
			this.activityStarted$$
		]).pipe(
			map(([sessions, activityStarted]) => {
				const baseCondition = this.getCurrentActivity().has_single_recording_attempt &&
					this.selectedService.getGroup().hasPresenterRole() &&
					sessions.some(
						(session: ReturnType<typeof SessionModel>) => (
							!session.archived_at &&
							!session.started_at &&
							session.isOwner(this.userService.currentUser) &&
							session.hasViewedRecordingInstructions()
						)
					);

				// Do not show view if activity started but navigation not complete
				// Otherwise, show immediately if base conditions are met
				return baseCondition && (!activityStarted);
			})
		);
		this.shouldShowFooterButtons$ = combineLatest({
			hasFilteredSessions: this.hasFilteredSessions$,
			isFiltered: this.sessionListDataSource.isFiltered$,
			shouldShowTestBailedView: this.shouldShowTestBailedView$
		}).pipe(
			map(({hasFilteredSessions, isFiltered, shouldShowTestBailedView}) =>
				(hasFilteredSessions || isFiltered) && !shouldShowTestBailedView
			)
		);

		this.form.get('sortBy').valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((selected) => this.sessionListDataSource.setSortBy(selected));

		this.getLicenseAndValidate();
		this.initializeLearnerTour();
	}

	public get isActivityTasksEnabled (): boolean {
		const activity = this.getCurrentActivity();
		return activity.show_tasks || this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	public getStartActivityButtonTextKey (): string {
		if (this.isActivityTasksEnabled) {
			return this.ngxActivityService.shouldUseReviewVideoAction(this.getCurrentActivity())
				? 'activity-view_review-video'
				: 'activity-view_create-video';
		}

		return 'session-zero-state_start-activity';
	}

	public getStartActivityButtonShortenedTextKey (): string {
		if (this.isActivityTasksEnabled) {
			return this.ngxActivityService.shouldUseReviewVideoAction(this.getCurrentActivity())
				? 'activity-view_review-video'
				: 'activity-view_create-video-shortened';
		}

		return 'session-zero-state_start-activity_shortened';
	}

	public getStartActivityButtonAriaLabelKey (): string {
		if (this.isActivityTasksEnabled) {
			return this.ngxActivityService.shouldUseReviewVideoAction(this.getCurrentActivity())
				? 'activity-view_review-video'
				: 'activity-view_create-a-video';
		}

		return 'session-zero-state_start-activity';
	}

	public onNavigateBack (): void {
		const previousTask = this.getAdjacentTask(-1);
		if (previousTask) {
			if (!this.isEditorContextMode) {
				this.activityViewComponent?.setNextRouteAnnouncementPoliteness('assertive');
			}
			this.applyTaskSelection(previousTask.type);
			this.navigateToTask(previousTask.type);
			return;
		}

		if (this.isEditorContextMode && this.currentTaskPosition === 2) {
			this.editorContext?.openTaskList?.();
		}
	}

	public onNavigateForward (): void {
		const nextTask = this.getAdjacentTask(1);
		if (nextTask) {
			if (!this.isEditorContextMode) {
				this.activityViewComponent?.setNextRouteAnnouncementPoliteness('assertive');
			}
			this.applyTaskSelection(nextTask.type);
			this.navigateToTask(nextTask.type);
		}
	}

	public onOpenNavigator (): void {
		if (this.isEditorContextMode) {
			this.editorContext.openTaskList?.();
			return;
		}

		this.ngxActivityService.openActivityInfoPanel().subscribe((task) => {
			if (task) {
				this.applyTaskSelection(task.type);
				this.navigateToTask(task.type);
			}
		});
	}

	public getTaskNavigatorBackLabelKey (): string {
		return this.isEditorContextMode && this.currentTaskPosition === 2
			? 'task-details_nav-back-to-list'
			: 'task-details_nav-back';
	}

	public setScoredFilter (scored: ScoreFilter) {
		this.scoredFilter$.next(scored);
		this.sessionListDataSource.setScoreFilter(scored);
	}

	public zeroStateChanged (event: ZeroStateChangedEvent) {
		this.dataSourceZeroState = event;
		this.cdr.detectChanges();
	}

	public shouldShowFeedbackHold () {
		const activity = this.getCurrentActivity();
		return this.hasInstructorRole() && activity && activity.isViewable('is_feedback_published');
	}

	public hasInstructorRole (): boolean {
		const editorGroup = this.getEditorContextGroup();
		if (this.editorContext != null) {
			return editorGroup?.hasInstructorRole?.(true) ?? false;
		}

		return this.selectedService.getGroup().hasInstructorRole(true);
	}

	public hasPresenterRole (): boolean {
		const editorGroup = this.getEditorContextGroup();
		if (this.editorContext != null) {
			return editorGroup?.hasPresenterRole?.() ?? false;
		}

		return this.selectedService.getGroup().hasPresenterRole();
	}

	public scoredActivity (): boolean {
		return this.getCurrentActivity().total_score > 0;
	}

	public isConversationBoard (): boolean {
		return this.getCurrentActivity().isConversationBoard();
	}

	public openMenu (event: MouseEvent) {
		if (this.editorContext != null) {
			return;
		}

		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}

		if (this.canStartActivity$$ && !this.getCurrentActivity().isConversationBoard()) {
			this.menuComponent.openMenu(event, this.activityContainer.nativeElement);
		}
	}

	public isLti (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public isReadOnly (): boolean {
		return this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY);
	}

	public openSession (session) {
		if (this.editorContext != null) {
			return;
		}

		const activity = this.getCurrentActivity();
		if (!session.isRecorded() && activity.isConversation() &&
			this.ngxSessionService.doesGroupSessionHaveMaxPresenterCount(session)) {
			return this.showSessionFullModal();
		} else if (activity.isSingleAttempt() && !activity.isCommentOnly() && !session.media) {
			return this.showFailedSingleAttemptModal();
		}

		this.enterSession(session);
	}

	public enterSession (session) {
		if (this.editorContext != null) {
			return;
		}

		const activity = this.getCurrentActivity();

		// live multi cam (group recording && live review)
		// prompt the user if they would like to be a presenter or observer
		if (activity.isMulticamLiveReview() && !session.isPosted()) {
			// If the group is already full, skip asking the user what they want and navigate to session
			if (!session.hasMaxPresenterCount(activity)) {
				this.confirmParticipation(session);
			} else {
				this.goToSession(session);
			}
		}
		// live review
		else if (activity.isLiveSessionEnabled() &&
			!activity.isConversation() &&
			session.isAwaitingStart() &&
			this.mayStart(session)
		) {
			this.confirmParticipation(session, true);
		}
		// group recording
		else if (activity.isConversation() &&
			!this.ngxSessionService.doesGroupSessionHaveMaxPresenterCount(session) &&
			!session.isRecorded()
		) {
			this.checkEquipment(session);
		} else {
			this.goToSession(session);
		}
	}

	public goToSession (session) {
		if (this.editorContext != null) {
			return;
		}

		this.selectedService.setSession(this.Session.model(session));
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
			session_id: session.session_id
		});
	}

	public preStartActivity () {
		if (this.editorContext != null) {
			return;
		}

		if (this.canStartActivity$$.value === false) {
			return;
		}

		const activity = this.getCurrentActivity();

		if (activity.isCommentOnlySingleAttempt()) {
			this.modal.open(StartTestDialogComponent, false, {
				data: {
					currentActivity: activity
				}
			}).afterClosed().subscribe((confirm) => {
				if (confirm) {
					this.startActivity();
				}
			});
		} else {
			this.startActivity();
		}
	}

	public startActivity () {
		if (this.editorContext != null) {
			return;
		}

		// clear out any previously selected session to prevent issues with recording
		// sessionCreationWizardService promise doesnt resolve until after recording instructions
		// are viewed, which happens after we go to the record screen
		// and we don't set the new session until after that
		this.selectedService.setSession(null);
		const activity = this.getCurrentActivity();
		const group = this.selectedService.getGroup();

		return this.sessionCreationWizardService.start({
			activity,
			group,
			user: this.userService.currentUser,
			openRecordView: (session) => {
				this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
					session_id: session.session_id
				}, {
					queryParams: {
						feedbackMode: FeedbackMode.VIEW,
						viewMode: MediaDisplayMode.RECORD
					}
				});
				return this.$q.resolve();
			}
		}).then((result) => {
			this.activityStarted$$.next(true);
			result.viewed = true;
			const session = this.Session.model(result);
			this.selectedService.setSession(session);
			this.sessionListDataSource.addSession(session);

			if (this.hasPresenterRole()) {
				this.activityListDataSource.reloadStats();
				this.folderListDataSource.reloadStats();
				// dont allow second submission for single attempt
				if (activity.has_single_recording_attempt) {
					this.getLicenseAndValidate();
				}
			}

			if (this.ngxActivityService.shouldUseReviewVideoAction(activity) || activity.isConversationBoard()) {
				this.goToSession(session);
			}
			return result;
		}).catch(noop);
	}

	public viewInstructions () {
		if (this.editorContext != null) {
			return;
		}

		this.modal.open(ActivityInstructionsDialogComponent);
	}

	public canInvite (): boolean {
		if (this.editorContext != null) {
			return false;
		}

		return !this.isLti() && (this.hasInstructorRole() || this.userService.currentUser.is_root_user);
	}

	public openInviteModal () {
		if (this.editorContext != null) {
			return;
		}

		InviteUsersDialogComponent.open(this.dialog, {
			data: {
				group: this.selectedService.getGroup()
			}
		});
	}

	public shouldShowPresentTaskAdditionalSettings (): boolean {
		return this.taskType$.value === TaskType.Present && this.isEditingActivity();
	}

	public shouldShowReactAdditionalSettings (): boolean {
		return this.taskType$.value === TaskType.React && this.isEditingActivity();
	}

	public onTasksUpdated (tasks: Task[]): void {
		this.editorContext?.updateTasks(tasks);
		this.updateTasks(tasks);
		this.applyTaskSelection(this.taskType$.value, false);
	}

	public shouldShowTaskDetailsSubtitle () {
		if (this.isEditorContextMode) {
			return false;
		}

		const currentRoute = this.routerService.getCurrentRouteName();
		const currentParams = this.routerService.getCurrentParams();
		return currentRoute === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK
			&& currentParams.type
			&& currentParams.type !== 'evaluate';
	}

	public shouldShowTaskTopSection (taskType: TaskType) {
		if (this.isEditorContextMode && taskType === TaskType.Evaluate) {
			return false;
		}

		if (taskType === TaskType.Evaluate) {
			return this.scoredActivity();
		}

		return true;
	}

	private initializeTaskStreams (): void {
		if (this.isEditorContextMode) {
			this.filteredSessions$ = of([]);
			this.learnFromFeedbackZeroState$ = of(null);
			this.currentView$ = of(VIEWS.LIST);
			this.hasFilteredSessions$ = of(false);
			this.shouldShowFooterButtons$ = of(false);
			return;
		}

		this.filteredSessions$ = combineLatest({
			sessions: this.sessionListDataSource.connect(),
			taskType: this.taskType$.pipe(filter((taskType): taskType is TaskType => taskType != null))
		}).pipe(
			map(
				({sessions, taskType}) =>
					this.filterSessionsByTaskType(
						sessions as ReturnType<typeof SessionModel>[],
						taskType
					)
			)
		);
		this.learnFromFeedbackZeroState$ = combineLatest({
			sessions: this.sessionListDataSource.connect(),
			taskType: this.taskType$.pipe(filter((taskType): taskType is TaskType => taskType != null))
		}).pipe(
			map(({sessions, taskType}) =>
				this.getLearnFromFeedbackZeroState(
					sessions as ReturnType<typeof SessionModel>[],
					taskType
				)
			),
			distinctUntilChanged()
		);
		this.currentView$ = this.viewStateDataSource.viewState$;
		this.hasFilteredSessions$ = this.filteredSessions$
			.pipe(
				map((sessions) => sessions.length > 0)
			);
	}

	private initializeEditorContextMode (): void {
		effect(() => {
			const activity = this.editorContext.activity();
			const selectedTaskType = this.editorContext.selectedTaskType();
			const tasks = this.editorContext.tasks();

			if (activity == null) {
				return;
			}

			this.activityId = activity.activity_id;
			this.updateTasks(tasks);
			if (selectedTaskType == null) {
				this.clearTaskSelection();
				return;
			}

			this.applyTaskSelection(selectedTaskType, false);
		}, { injector: this.injector });
	}

	private updateTasks (tasks: Task[]): void {
		const previewTasks = this.isEditorContextMode
			? tasks.filter((task) => task.type !== TaskType.Evaluate && task.deleted_at == null)
			: tasks;
		this.tasks = [...previewTasks].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
		this.totalTaskCount = this.isEditorContextMode && this.tasks.length > 0
			? this.tasks.length + 1
			: this.tasks.length;
		if (!this.isEditorContextMode) {
			this.activityEditModeService.setOriginalTasks(this.tasks); // Keep service in sync with loaded tasks
		}
	}

	private clearTaskSelection (): void {
		this.taskType$.next(null);
		this.task = null;
		this.currentTaskPosition = 0;
	}

	private applyTaskSelection (taskType: TaskType, updateFilter: boolean = true): void {
		if (taskType == null || (this.isEditorContextMode && taskType === TaskType.Evaluate)) {
			return;
		}

		this.taskType$.next(taskType);
		const tasks = this.getCurrentTasks();
		const currentIndex = tasks.findIndex((task) => task.type === taskType);
		this.task = currentIndex >= 0 ? tasks[currentIndex] : null;
		this.currentTaskPosition = currentIndex >= 0
			? currentIndex + (this.isEditorContextMode ? 2 : 1)
			: 0;
		if (taskType !== TaskType.Submit) {
			this.resetSubmitSelectionState();
		} else {
			this.syncSubmitSelectionState();
		}

		if (updateFilter && !this.isEditorContextMode) {
			this.setMinePeerFilter();
			this.setExcludeUnsubmittedFilter();
			this.keepScoreFilterInSync();
		}

	}

	private getAdjacentTask (direction: -1 | 1): Task | null {
		if (!this.task) {
			return null;
		}

		const currentIndex = this.tasks.findIndex((task) => task.type === this.taskType$.value);
		if (currentIndex === -1) {
			return null;
		}

		const targetIndex = currentIndex + direction;
		if (targetIndex < 0 || targetIndex >= this.tasks.length) {
			return null;
		}

		return this.tasks[targetIndex];
	}

	private navigateToTask (taskType: TaskType): void {
		if (this.editorContext != null) {
			this.editorContext.selectTask(taskType);
			return;
		}

		const currentQueryParams = this.routerService.getCurrentQueryParams();
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
			activity_id: this.activityId,
			type: taskType
		}, {
			queryParams: currentQueryParams
		});
	}

	private getCurrentActivity (): any {
		if (this.editorContext != null) {
			return this.editorContext.activity();
		}

		return this.selectedService.getActivity();
	}

	private getEditorContextGroup (): ActivityTaskSessionsGroupContext | null {
		const group = this.editorContext?.group?.();
		return group == null ? null : group as ActivityTaskSessionsGroupContext;
	}

	private getCurrentTasks (): Task[] {
		return this.editorContext?.tasks() ?? this.activityEditModeService.currentTasks();
	}

	private initializeSessionListControls (): void {
		this.form.get('sortBy').setValue(this.sessionListDataSource.getCurrentSort(), { emitEvent: false });
		this.scoredFilter$.next(this.sessionListDataSource.getCurrentScoreFilter());
	}

	private isEditingActivity (): boolean {
		return this.editorContext?.isEditMode() ?? this.activityEditModeService.isEditMode();
	}

	private validateCanStartActivity () {
		const canStartActivity = this.ngxActivityService.canStartActivity();
		this.canStartActivity$$.next(canStartActivity);
		this.getSessionCreationDisabledMessage$$.next(this.ngxActivityService.getSessionCreationDisabledMessage());

		const license = this.selectedService.getLicense();
		if (license) {
			// TODO STAB-489 Consolidate ngx/ng1 License interfaces since we're already using them interchangeably
			this.licenseExpirationHandler.displayLicenseExpirationWarningToastIfNeeded(license as License);
		}
	}

	private getLicenseAndValidate () {
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.destroy$),
				filter((selected: any) => selected.group)
			)
			.subscribe((selected) => {
				if (this.isLti() &&
					this.hasInstructorRole() &&
					this.featureFlag.isAvailable('AI_TRIAL') &&
					selected.group.license_trial_exists &&
					selected.license?.salesforce_license.is_free_trial &&
					(!selected.license.is_active || dayjs().isAfter(dayjs.utc(selected.license.ends_at)))) {
					this.handleExpiredLicenseTrial(selected.license).subscribe();
				}
				this.validateCanStartActivity();
			});
	}

	private handleExpiredLicenseTrial (license: License) {
		return this.ngxLicenseUpgradeService.upgradeOrPurchase(license.trial.previous_license, true, true, true)
			.pipe(
				switchMap(({requestUpgrade, shouldRestart}) => {
					if (shouldRestart) {
						return this.handleExpiredLicenseTrial(license);
					} else if (requestUpgrade) {
						// Non license-admin requested an upgrade
						return this.ngxLicenseUpgradeService
							.handleLicenseUpgradeRequest(license.trial.previous_license.id);
					} else {
						// License admin successfully upgraded
						return EMPTY;
					}
				}),
				takeUntil(this.destroy$)
			);
	}

	private showSessionFullModal (): void {
		MessageDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('session-list-item_controller-max-presenter-title'),
				message: this.translate.instant('session-list-item_controller-max-presenter-message')
			}
		});
	}

	private showFailedSingleAttemptModal () {
		MessageDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('single-attempt-activity_no-recording'),
				message: this.translate.instant('feedback-session-failed-single-attempt')
			}
		});
	}

	private confirmParticipation (session, isLiveReview = false) {
		const user = this.userService.currentUser;
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('confirm-join-title'),
				message: this.translate.instant('confirm-join-message'),
				confirmText: this.translate.instant('confirm-join-button-as-presenter'),
				cancelText: this.translate.instant('confirm-join-button-as-observer')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				if (isLiveReview) {
					this.startLiveReview(session);
				} else {
					this.checkEquipment(session);
				}
			}
			/**
			 * confirm dialog can return null on close
			 * explicitly check if false is returned
			 */
			else if (confirm === false) {
				session.removePresenter(user);
				this.sessionListDataSource.editSession(session);
				this.goToSession(session);
			}
		});
	}

	private startLiveReview (session) {
		let startPromise = Promise.resolve(null);

		// If media has not been created yet, pre-create the media and assign it to the session.
		// This should only get run when the session was created as a part of a batch creation process.
		// When the Live Review setting is enabled on an assignment,
		// the Instructor can click a button that creates a batch of sessions
		if (!session.media) {
			startPromise = new Promise(async (resolve, _reject) => {
				// MediaModel.createFor() contains async code
				const media = await this.MediaModel.createFor(MediaSource.OPENTOK, session.group_id);
				session.setMedia(media);
				session.save();
				resolve(null);
			});
		}

		startPromise.then(() => {
			this.showInstructionsThenGoToSession(session);
		});
	}

	private mayStart (session) {
		const activity = this.getCurrentActivity();
		const group = this.selectedService.getGroup();
		const user = this.userService.currentUser;
		let hasPermission = session.mayPublish(user, group, activity);

		if (session.source_media?.isPending()) {
			hasPermission = false;
		}

		return hasPermission;
	}

	private showInstructionsThenGoToSession (session) {
		const activity = this.getCurrentActivity();
		const user = this.userService.currentUser;

		if (activity.hasRecordingInstructions() || this.isHeadPhonesInstructionsRequired(session)) {
			const group = this.selectedService.getGroup();
			const embiggen = activity.hasRecordingInstructions() &&
				!!activity.recording_instructions.media &&
				!group.license_trial_exists;
			const modalInstance = this.activityInstructionsModal.open({
				focusReturnSelector: '[data-cy="comment-tab-button"]',
				modalData: {
					activity,
					mode: INSTRUCTIONS_MODE.RECORDING,
					showHeadPhones: this.isHeadPhonesInstructionsRequired(session),
					embiggen
				}
			});
			modalInstance.opened
				.then(() => {
					if (!session.hasViewedRecordingInstructions()) {
						session.setViewedRecordingInstructions();
					}
				});

			modalInstance.result.then(() => {
				this.addPresenterThenGoToRecordSession(session, user);
			});
		} else {
			this.addPresenterThenGoToRecordSession(session, user);
		}
	}

	private addPresenterThenGoToRecordSession (session, user) {
		return Promise.resolve(session.addPresenter(user)).then(() => {
			this.sessionListDataSource.editSession(session);
			this.selectedService.setSession(session);
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
				session_id: session.session_id
			}, {
				queryParams: {
					feedbackMode: FeedbackMode.VIEW,
					viewMode: MediaDisplayMode.RECORD
				}
			});
		});
	}

	private checkEquipment (session) {
		const activity = this.getCurrentActivity();
		const isEquipmentCheckRequired = activity.isEquipmentCheckRequired(this.sessionStorage);

		if (isEquipmentCheckRequired) {
			try {
				const equipmentCheck: any = this.equipmentCheckModal.open({
					activity,
					session,
					focusReturnSelector: '[data-cy="add-video"]'
				});
				equipmentCheck.result
					.then(() => {
						this.showInstructionsThenGoToSession(session);
					});
			} catch (e) {
				MessageDialogComponent.open(this.dialog, {
					data: {
						title: this.translate.instant('unknown-error-title'),
						message: this.translate.instant('group-view_zero-state-unexpected-error')
					}
				});
			}
		} else {
			this.showInstructionsThenGoToSession(session);
		}
	}

	private isHeadPhonesInstructionsRequired (session) {
		const media = session.source_media || this.getCurrentActivity().source_media?.media;
		return !!media && media.hasAudio();
	}

	public onSessionSelectionToggled (session: ReturnType<typeof SessionModel>): void {
		if (this.editorContext != null || !session || !session.session_id || this.taskType$.value !== TaskType.Submit) {
			return;
		}

		this.submitSelectionDirty = true;
		const updatedSelection = new Set(this.selectedSessionIds$.value);
		if (updatedSelection.has(session.session_id)) {
			updatedSelection.delete(session.session_id);
		} else {
			updatedSelection.add(session.session_id);
		}

		this.selectedSessionIds$.next(updatedSelection);
		this.hasSelectedSessions = updatedSelection.size > 0;
	}

	public submitSelectedSessions (): void {
		if (
			this.editorContext != null ||
			this.taskType$.value !== TaskType.Submit ||
			this.isSubmittingSessions
		) {
			return;
		}

		const selectedIds = Array.from(this.selectedSessionIds$.value);
		if (!this.hasSubmittedSessions$.value && selectedIds.length === 0) {
			return;
		}

		this.isSubmittingSessions = true;
		this.ngxSessionService.syncSubmitSessions(this.activityId, selectedIds)
			.pipe(finalize(() => {
				this.isSubmittingSessions = false;
				this.cdr.markForCheck();
			}))
			.subscribe({
				next: () => {
					this.submitSelectionDirty = false;
					this.submitSelectionInitialized = false;
					this.isEditingSubmission = false;
					this.ngxGoToastService.createToast({
						type: 'success',
						message: this.translate.instant('activity-task-sessions_submit-success')
					});
					this.sessionListDataSource.reload();
				},
				error: (error) => {
					const message = error?.status === HttpStatusCode.Forbidden ?
						this.translate.instant('activity-task-sessions_unsubmit-forbidden') :
						(error?.error?.message ?? this.translate.instant('activity-task-sessions_submit-error'));
					this.ngxGoToastService.createToast({
						type: 'error',
						message
					});
				}
			});
	}

	private syncSubmitSelectionState (force = false): void {
		if (this.taskType$.value !== TaskType.Submit) {
			this.resetSubmitSelectionState();
			return;
		}

		if (!force && (this.submitSelectionDirty || this.submitSelectionInitialized)) {
			return;
		}

		if (!this.latestSessions?.length) {
			return;
		}

		const submittedSessions = this.latestSessions
			.filter((session) => session.isPosted())
			.map((session) => session.session_id);

		this.selectedSessionIds$.next(new Set(submittedSessions));
		this.submitSelectionInitialized = true;
	}

	private resetSubmitSelectionState (): void {
		this.submitSelectionInitialized = false;
		this.submitSelectionDirty = false;
		if (this.selectedSessionIds$.value.size > 0) {
			this.selectedSessionIds$.next(new Set());
		}
	}

	private updateHasSubmittedSessions (): void {
		const hasSubmitted = this.latestSessions.some((session) => session.isPosted());
		this.hasSubmittedSessions$.next(hasSubmitted);
		if (!hasSubmitted) {
			this.isEditingSubmission = false;
		}
	}

	public enterEditSubmissionMode (): void {
		if (this.editorContext != null || this.isReadOnly()) {
			return;
		}

		this.isEditingSubmission = true;
	}

	public cancelEditSubmissionMode (): void {
		if (this.editorContext != null) {
			return;
		}

		this.submitSelectionDirty = false;
		this.submitSelectionInitialized = false;
		this.syncSubmitSelectionState(true);
		this.isEditingSubmission = false;
	}

	public shouldAllowSessionSelection (hasSubmittedSessions: boolean | null): boolean {
		return this.isEditingSubmission || !(hasSubmittedSessions ?? false);
	}

	public getNoVideosTitleKey (): string {
		const taskType = this.taskType$.value;
		if (taskType === TaskType.Evaluate) {
			if (this.isGroupActivity()) {
				return 'activity-view_create-a-video';
			}

			return 'session-zero-state_no-submissions';
		}
		if (taskType === TaskType.React) {
			return 'session-zero-state_no-submissions-peer';
		}
		return 'session-zero-state_no-videos';
	}

	public getNoVideosSubtextKey (): string {
		const taskType = this.taskType$.value;
		if (taskType === TaskType.Evaluate) {
			if (this.isGroupActivity() && this.canInvite()) {
				return 'session-zero-state_group-no-submissions-subtext';
			}

			return this.canInvite()
				? 'session-zero-state_no-submissions-subtext'
				: 'session-zero-state_no-submissions-subtext-lti';
		}
		return 'session-zero-state_no-videos-instructor-subtext';
	}

	public getNoVideosSubtextParams (): Record<string, unknown> {
		return this.isGroupActivity() && this.canInvite()
			? { groupName: this.selectedService.getGroup().name }
			: {};
	}

	private setMinePeerFilter () {
		if (this.isEditorContextMode) {
			return;
		}

		const activity = this.getCurrentActivity();
		const taskType = this.taskType$.value;
		if (activity == null || taskType == null) {
			return;
		}

		if (taskType === TaskType.Present) {
			if (
				(activity.peer_review && this.isLiveSessionEnabled(activity)) ||
				this.isConversationBoardActivity(activity)
			) {
				this.sessionListDataSource.setMinePeerFilter(MinePeerFilter.ALL);
			} else {
				this.sessionListDataSource.setMinePeerFilter(MinePeerFilter.MINE);
			}
		} else if ([TaskType.Reflect, TaskType.Submit, TaskType.Review].includes(taskType)) {
			this.sessionListDataSource.setMinePeerFilter(MinePeerFilter.MINE);
		} else if (taskType === TaskType.React) {
			this.sessionListDataSource.setMinePeerFilter(MinePeerFilter.PEER);
		} else if (taskType === TaskType.Evaluate) {
			this.sessionListDataSource.setMinePeerFilter(MinePeerFilter.ALL);
		}
	}

	private setExcludeUnsubmittedFilter (): void {
		const activity = this.getCurrentActivity();
		this.sessionListDataSource.setExcludeUnsubmitted(
			this.taskType$.value === TaskType.Evaluate && !this.isLiveSessionEnabled(activity)
		);
	}

	private keepScoreFilterInSync () {
		if (this.isEditorContextMode) {
			return;
		}

		// we only care about score filter for evaluate task
		// so if not evaluate task, set score filter to all
		if (this.taskType$.value !== TaskType.Evaluate) {
			this.sessionListDataSource.setScoreFilter(ScoreFilter.ALL);
			return;
		}

		const activity = this.getCurrentActivity();
		if (activity == null) {
			return;
		}

		if (activity.total_score > 0) {
			const scoredFilter = this.scoredFilter$.value;
			// if the score filter is all, but activity is scored, set the score filter to no score
			if (scoredFilter === ScoreFilter.ALL) {
				this.setScoredFilter(ScoreFilter.NO_SCORE);
			// otherwise keep filter in sync with the selected tab
			} else {
				this.sessionListDataSource.setScoreFilter(scoredFilter);
			}
		} else {
			this.sessionListDataSource.setScoreFilter(ScoreFilter.ALL);
		}
	}

	private isLiveSessionEnabled (activity: any): boolean {
		return typeof activity.isLiveSessionEnabled === 'function'
			? activity.isLiveSessionEnabled()
			: activity.live_session_enabled === true;
	}

	public isGroupActivity (): boolean {
		const activity = this.getCurrentActivity();
		if (activity == null) {
			return false;
		}

		const isConversation = typeof activity.isConversation === 'function'
			? activity.isConversation()
			: activity.is_conversation;

		return isConversation && activity.has_response_media && !this.isLiveSessionEnabled(activity);
	}

	private isConversationBoardActivity (activity: any): boolean {
		return typeof activity.isConversationBoard === 'function'
			? activity.isConversationBoard()
			: false;
	}

	private filterSessionsByTaskType (
		sessions: ReturnType<typeof SessionModel>[],
		taskType: TaskType
	): ReturnType<typeof SessionModel>[] {
		if (taskType === TaskType.Review) {
			const activity = this.getCurrentActivity();
			if (activity.is_feedback_published === false) {
				return [];
			}

			return sessions.filter((session) => {
				return session.posted_at != null && this.hasOtherFeedback(session);
			});
		}
		return sessions;
	}

	private getLearnFromFeedbackZeroState (
		sessions: ReturnType<typeof SessionModel>[],
		taskType: TaskType
	): LearnFromFeedbackZeroState | null {
		if (taskType !== TaskType.Review || !sessions.length) {
			return null;
		}

		const postedSessions = sessions.filter((session) => session.posted_at != null);
		if (!postedSessions.length) {
			return LearnFromFeedbackZeroState.NOT_SUBMITTED;
		}

		const activity = this.getCurrentActivity();
		if (activity.is_feedback_published === false) {
			return LearnFromFeedbackZeroState.FEEDBACK_HELD;
		}

		const sessionsWithFeedback = postedSessions.filter((session) => this.hasOtherFeedback(session));
		if (!sessionsWithFeedback.length) {
			return LearnFromFeedbackZeroState.NO_FEEDBACK_YET;
		}

		return null;
	}

	private hasOtherFeedback (session: ReturnType<typeof SessionModel>): boolean {
		return this.getNumOtherComments(session) > 0 || this.getNumOtherRubrics(session) > 0;
	}

	private getNumOtherComments (session: ReturnType<typeof SessionModel>): number {
		const numComments = Number(session.num_comments) || 0;
		const numMyComments = Number(session.num_my_comments) || 0;
		const numAiComments = Number(session.num_ai_comments) || 0;
		const nonAiComments = Math.max(numComments - numAiComments, 0);
		return Math.max(nonAiComments - numMyComments, 0);
	}

	private getNumOtherRubrics (session: ReturnType<typeof SessionModel>): number {
		const numRubrics = Number(session.num_rubrics) || 0;
		const numMyRubrics = Number(session.num_my_rubrics) || 0;
		return Math.max(numRubrics - numMyRubrics, 0);
	}

	/**
	 * Initializes the learner tour for activity tasks.
	 *
	 * Behavior:
	 * - If all tasks are complete, don't start the tour (can appear on another activity)
	 * - Pulse the first incomplete task (if user is not already on it)
	 * - When user clicks pulsing task and navigates to it, pulse the next incomplete task
	 * - Tour completes when user clicks the LAST incomplete task (only 1 remaining)
	 */
	private initializeLearnerTour (): void {
		// Only show tour for learners (presenters)
		if (!this.selectedService.getGroup().hasPresenterRole()) {
			return;
		}

		// Combine tasks and current task type
		combineLatest({
			tasks: this.taskService.getTasks(this.activityId),
			taskType: this.taskType$.pipe(
				filter((t): t is TaskType => t != null)
			)
		}).pipe(
			// Map to the relevant state for tour decisions
			map(({ tasks, taskType }) => {
				const sortedTasks = [...tasks].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
				const incompleteTasks = sortedTasks.filter((task) => !task.completed);
				const firstIncompleteTask = incompleteTasks[0] ?? null;
				return {
					currentTaskType: taskType,
					firstIncompleteTaskType: firstIncompleteTask?.type ?? null,
					incompleteTaskCount: incompleteTasks.length
				};
			}),
			// Only emit when the tour-relevant state actually changes
			distinctUntilChanged((prev, curr) =>
				prev.currentTaskType === curr.currentTaskType &&
				prev.firstIncompleteTaskType === curr.firstIncompleteTaskType &&
				prev.incompleteTaskCount === curr.incompleteTaskCount
			),
			takeUntil(this.destroy$)
		).subscribe(({ currentTaskType, firstIncompleteTaskType, incompleteTaskCount }) => {
			if (firstIncompleteTaskType == null) {
				this.pulseTourService.cleanupTour(ACTIVITY_TASK_LEARNER_TOUR_KEY);
				return;
			}

			if (currentTaskType === firstIncompleteTaskType) {
				if (incompleteTaskCount === 1) {
					this.pulseTourService.completeTour(ACTIVITY_TASK_LEARNER_TOUR_KEY);
				}
				this.pulseTourService.cleanupTour(ACTIVITY_TASK_LEARNER_TOUR_KEY);
				return;
			}

			this.pulseTourService.cleanupTour(ACTIVITY_TASK_LEARNER_TOUR_KEY);
			this.startLearnerTour();
		});
	}

	/**
	 * Starts the learner tour to pulse the first incomplete task.
	 */
	private startLearnerTour (): void {
		const steps = [createPulseIncompleteTaskStep()];
		const tourConfig = createActivityTaskLearnerTour(steps);
		this.pulseTourService.startTour(tourConfig)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					return EMPTY;
				})
			)
			.subscribe();
	}

}
